import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import api from '../lib/api';

const WarehouseManagement = ({ user }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', address: '', area: '', city: '', state: '', pincode: '', manager_name: '', phone: '' });
  
  const [warehouseForm, setWarehouseForm] = useState({
    name: '',
    address: '',
    area: '',
    city: '',
    state: '',
    pincode: '',
    manager_name: '',
    phone: ''
  });
  const [availableAreas, setAvailableAreas] = useState([]);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const response = await api.get('/warehouses');
      setWarehouses(response.data);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast.error('Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWarehouseForm(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'pincode' && value.length === 6) {
      fetchPincodeDetails(value);
    }
  };

  const fetchPincodeDetails = async (pincode) => {
    try {
      const response = await api.get(`/pincode/${pincode}`);
      const { areas, area, city, state } = response.data;
      setAvailableAreas(areas || []);
      setWarehouseForm(prev => ({
        ...prev,
        area: area || '',
        city: city || '',
        state: state || ''
      }));
      toast.success(`Auto-filled: ${area}, ${city}, ${state}`);
    } catch (error) {
      console.error('Error fetching pincode details:', error);
      if (error.response?.status === 404) {
        toast.error('Pincode not found. Please enter details manually.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/warehouses', warehouseForm);
      
      toast.success('Warehouse created successfully!');
      setIsDialogOpen(false);
      resetForm();
      fetchWarehouses();
    } catch (error) {
      console.error('Error creating warehouse:', error);
      const message = error.response?.data?.detail || 'Failed to create warehouse';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setWarehouseForm({
      name: '',
      address: '',
      area: '',
      city: '',
      state: '',
      pincode: '',
      manager_name: '',
      phone: ''
    });
    setAvailableAreas([]);
  };

  const startEditWarehouse = (wh) => {
    setEditForm({
      name: wh.name || '', address: wh.address || '', area: wh.area || '',
      city: wh.city || '', state: wh.state || '', pincode: wh.pincode || '',
      manager_name: wh.manager_name || '', phone: wh.phone || '',
    });
    setEditingWarehouse(wh);
  };

  const saveEditWarehouse = async () => {
    if (!editingWarehouse) return;
    try {
      await api.put(`/warehouses/${editingWarehouse.id}`, editForm);
      toast.success(`Warehouse "${editForm.name}" updated!`);
      setEditingWarehouse(null);
      fetchWarehouses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update warehouse'); }
  };

  const deleteWarehouse = async (wh) => {
    try {
      await api.delete(`/warehouses/${wh.id}`);
      toast.success(`Warehouse "${wh.name}" deleted!`);
      fetchWarehouses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete warehouse'); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  };

  const canManageWarehouses = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="warehouse-management-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>
            🏪 Warehouse Management
          </h1>
          <p className="text-gray-600 mt-2">Manage your storage facilities and inventory locations</p>
        </div>
        
        {canManageWarehouses && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
                data-testid="add-warehouse-button"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Warehouse
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Warehouse</DialogTitle>
                <DialogDescription>
                  Add a new warehouse to your network
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Warehouse Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={warehouseForm.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Main Distribution Center"
                    required
                    data-testid="warehouse-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={warehouseForm.address}
                    onChange={handleInputChange}
                    placeholder="Street address"
                    data-testid="warehouse-address-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode (Auto-fills Area, City & State)</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      value={warehouseForm.pincode}
                      onChange={handleInputChange}
                      placeholder="e.g., 400028"
                      maxLength="6"
                      data-testid="warehouse-pincode-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="area">Area</Label>
                    {availableAreas.length > 0 ? (
                      <select
                        id="area"
                        name="area"
                        value={warehouseForm.area}
                        onChange={handleInputChange}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        data-testid="warehouse-area-select"
                      >
                        {availableAreas.map((a, i) => (
                          <option key={i} value={a}>{a}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        id="area"
                        name="area"
                        value={warehouseForm.area}
                        onChange={handleInputChange}
                        placeholder="Auto-filled from pincode"
                        data-testid="warehouse-area-input"
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={warehouseForm.city}
                      onChange={handleInputChange}
                      placeholder="Auto-filled from pincode"
                      data-testid="warehouse-city-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      name="state"
                      value={warehouseForm.state}
                      onChange={handleInputChange}
                      placeholder="Auto-filled from pincode"
                      data-testid="warehouse-state-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="manager_name">Manager Name</Label>
                    <Input
                      id="manager_name"
                      name="manager_name"
                      value={warehouseForm.manager_name}
                      onChange={handleInputChange}
                      placeholder="e.g., Suresh Kumar"
                      data-testid="warehouse-manager-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={warehouseForm.phone}
                      onChange={handleInputChange}
                      placeholder="+91 9876543210"
                      data-testid="warehouse-phone-input"
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-6">
                  <Button type="submit" className="flex-1" data-testid="submit-warehouse-button">
                    Create Warehouse
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Warehouses List */}
      <Card data-testid="warehouses-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>🏪</span> Warehouses ({warehouses.length})
          </CardTitle>
          <CardDescription>
            Your storage facilities and inventory locations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {warehouses.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏪</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Warehouses Yet</h3>
              <p className="text-gray-600 mb-6">Set up your first warehouse to start managing inventory locations.</p>
              {canManageWarehouses && (
                <Button 
                  onClick={() => setIsDialogOpen(true)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600"
                >
                  Add Your First Warehouse
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {warehouses.map((warehouse) => (
                <Card key={warehouse.id} className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-purple-500" data-testid={`warehouse-card-${warehouse.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center">
                            <span className="text-2xl">🏪</span>
                          </div>
                          <CardTitle className="text-lg font-bold text-gray-900">
                            {warehouse.name}
                          </CardTitle>
                        </div>
                      </div>
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        Active
                      </Badge>
                      {canManageWarehouses && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50 h-7 w-7 p-0"
                            onClick={() => startEditWarehouse(warehouse)} data-testid={`edit-warehouse-${warehouse.id}`}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 h-7 w-7 p-0" data-testid={`delete-warehouse-${warehouse.id}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Warehouse</AlertDialogTitle>
                                <AlertDialogDescription>Are you sure you want to delete "{warehouse.name}"? This action cannot be undone.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteWarehouse(warehouse)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-warehouse-${warehouse.id}`}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      {warehouse.manager_name && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm text-gray-600">{warehouse.manager_name}</span>
                        </div>
                      )}
                      
                      {warehouse.phone && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span className="text-sm text-gray-600">{warehouse.phone}</span>
                        </div>
                      )}
                      
                      {(warehouse.address || warehouse.city || warehouse.area) && (
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <div className="text-sm text-gray-600">
                            {warehouse.address && <p>{warehouse.address}</p>}
                            {warehouse.area && <p>{warehouse.area}</p>}
                            {(warehouse.city || warehouse.state || warehouse.pincode) && (
                              <p>{[warehouse.city, warehouse.state].filter(Boolean).join(', ')}{warehouse.pincode ? ` - ${warehouse.pincode}` : ''}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        Established on {formatDate(warehouse.created_at)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Warehouse Dialog */}
      <Dialog open={!!editingWarehouse} onOpenChange={v => { if (!v) setEditingWarehouse(null); }}>
        <DialogContent className="max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Warehouse</DialogTitle>
            <DialogDescription>Update warehouse details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Warehouse Name *</Label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} placeholder="Warehouse name" data-testid="edit-warehouse-name" />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={editForm.address} onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} placeholder="Street address" data-testid="edit-warehouse-address" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pincode</Label>
                <Input value={editForm.pincode} onChange={e => setEditForm(p => ({ ...p, pincode: e.target.value }))} placeholder="e.g., 400028" maxLength="6" data-testid="edit-warehouse-pincode" />
              </div>
              <div className="space-y-2">
                <Label>Area</Label>
                <Input value={editForm.area} onChange={e => setEditForm(p => ({ ...p, area: e.target.value }))} placeholder="Area" data-testid="edit-warehouse-area" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} placeholder="City" data-testid="edit-warehouse-city" />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={editForm.state} onChange={e => setEditForm(p => ({ ...p, state: e.target.value }))} placeholder="State" data-testid="edit-warehouse-state" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Manager Name</Label>
                <Input value={editForm.manager_name} onChange={e => setEditForm(p => ({ ...p, manager_name: e.target.value }))} placeholder="Manager name" data-testid="edit-warehouse-manager" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91 9876543210" data-testid="edit-warehouse-phone" />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setEditingWarehouse(null)}>Cancel</Button>
              <Button onClick={saveEditWarehouse} disabled={!editForm.name.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-warehouse">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseManagement;